<template>
  <div class="overtime-clock">
    <!-- Active Session Display -->
    <div v-if="activeSession" class="active-session-card">
      <div class="session-header">
        <div class="session-status">
          <div :class="['status-indicator', activeSession.status]">
            <Clock class="w-5 h-5" />
            <span>{{ getStatusLabel(activeSession.status) }}</span>
          </div>
        </div>

        <div v-if="activeSession.status === 'active'" class="timer-display">
          <Timer class="w-6 h-6" />
          <div class="timer-content">
            <h2>{{ timeRemaining }}</h2>
            <p>Time Remaining</p>
          </div>
        </div>
      </div>

      <div class="session-details">
        <div class="detail-item">
          <Clock class="w-4 h-4" />
          <span>Clocked in at {{ formatTime(activeSession.clock_in_timestamp) }}</span>
        </div>
        <div class="detail-item">
          <MapPin class="w-4 h-4" />
          <span>{{ activeSession.clock_in_address || 'Location recorded' }}</span>
        </div>
        <div class="detail-item">
          <AlarmClock class="w-4 h-4" />
          <span>Timer ends at {{ formatTime(activeSession.timer_end_time) }}</span>
        </div>
        <div class="detail-item">
          <DollarSign class="w-4 h-4" />
          <span>Rate: {{ activeSession.overtime_rate_multiplier }}x</span>
        </div>
      </div>

      <!-- UX-ATT-003: Timer Expiry Warning -->
      <div v-if="activeSession.status === 'active' && expiryWarningVisible" class="expiry-warning">
        <AlertTriangle class="w-5 h-5" />
        <div>
          <p class="font-semibold">Timer expiring soon!</p>
          <p class="text-sm">Your overtime session expires in {{ timeRemaining }}. Clock out now to avoid auto-expiry.</p>
        </div>
      </div>

      <!-- Clock Out Button -->
      <div v-if="activeSession.status === 'active'" class="action-section">
        <button
          @click="showClockOutModal = true"
          :disabled="processing"
          class="btn-clock-out"
        >
          <LogOut class="w-5 h-5" />
          {{ processing ? 'Processing...' : 'Clock Out from Overtime' }}
        </button>
        <p class="hint-text">Clock out when you're done with your overtime work</p>
      </div>

      <!-- Pending Approval Message -->
      <div v-else-if="activeSession.status === 'pending'" class="pending-message">
        <AlertCircle class="w-5 h-5" />
        <div>
          <p class="font-semibold">Waiting for Admin Approval</p>
          <p class="text-sm">Your overtime request is being reviewed</p>
        </div>
        <button @click="cancelRequest(activeSession.id)" class="btn-cancel-small">
          Cancel Request
        </button>
      </div>

      <!-- OA-006: Approved Pre-Scheduled - Awaiting Activation -->
      <div v-else-if="activeSession.status === 'approved' && activeSession.mode === 'pre_scheduled'" class="activation-section">
        <div class="activation-header">
          <AlertCircle class="w-5 h-5" />
          <div>
            <p class="font-semibold">Pre-Scheduled Overtime Approved</p>
            <p class="text-sm">Scheduled: {{ formatScheduledDate(activeSession.scheduled_date) }} from {{ activeSession.scheduled_start_time }} to {{ activeSession.scheduled_end_time }}</p>
          </div>
        </div>

        <div v-if="isWithinActivationWindow" class="activation-form">
          <p class="activation-prompt">Take a photo and confirm your location to activate this overtime session.</p>

          <!-- Photo Capture for Activation -->
          <div class="photo-section">
            <label class="form-label">Photo Verification</label>
            <div v-if="!activationPhoto && !activationCameraActive" class="photo-placeholder" @click="startActivationCamera">
              <Camera class="w-8 h-8" />
              <span>Tap to start camera</span>
            </div>
            <div v-else-if="activationCameraActive && !activationPhoto" class="camera-view">
              <video ref="activationVideoElement" autoplay playsinline class="camera-preview"></video>
              <div class="camera-controls">
                <button @click="captureActivationPhoto" class="btn-capture" :disabled="!activationCameraReady">
                  <Camera class="w-5 h-5" />
                  Capture
                </button>
              </div>
            </div>
            <div v-else-if="activationPhoto" class="photo-preview">
              <img :src="activationPhoto" alt="Activation photo" class="captured-photo" />
              <button @click="retakeActivationPhoto" class="btn-retake">
                <RotateCcw class="w-4 h-4" />
                Retake
              </button>
            </div>
          </div>

          <!-- Location -->
          <div class="location-info" v-if="locationData">
            <MapPin class="w-4 h-4" />
            <span>Location: {{ locationData.latitude.toFixed(4) }}, {{ locationData.longitude.toFixed(4) }}</span>
          </div>
          <div class="location-info location-pending" v-else>
            <MapPin class="w-4 h-4" />
            <span>Detecting location...</span>
          </div>

          <!-- Activate Button -->
          <button
            @click="activateSession"
            :disabled="!canActivate || processing"
            class="btn-activate"
          >
            <Send class="w-5 h-5" />
            {{ processing ? 'Activating...' : 'Activate Overtime Session' }}
          </button>
        </div>

        <div v-else class="activation-window-info">
          <p class="text-sm">Activation window opens 15 minutes before your scheduled start time ({{ activeSession.scheduled_start_time }}).</p>
        </div>

        <button @click="cancelRequest(activeSession.id)" class="btn-cancel-small" style="margin-top: 12px;">
          Cancel Request
        </button>
      </div>

      <!-- Under Review Message -->
      <div v-else-if="activeSession.status === 'under_review'" class="review-message">
        <CheckCircle class="w-5 h-5" />
        <div>
          <p class="font-semibold">Session Under Review</p>
          <p class="text-sm">You've clocked out. Admin will review for payroll approval.</p>
        </div>
      </div>
    </div>

    <!-- Clock In Request Form -->
    <div v-else class="clock-in-section">
      <div class="section-header">
        <h3>Request Overtime Clock-In</h3>
        <p>Submit a request to work overtime hours</p>
      </div>

      <!-- Error/Success Message -->
      <div v-if="message" :class="['message', messageType]">
        <div class="message-content">
          <CheckCircle v-if="messageType === 'success'" class="w-5 h-5" />
          <AlertCircle v-else class="w-5 h-5" />
          <span>{{ message }}</span>
        </div>
        <button @click="clearMessage" class="message-close">
          <X class="w-4 h-4" />
        </button>
      </div>

      <div class="request-form">
        <!-- Duration Input -->
        <div class="form-group">
          <label>Requested Duration (minutes) *</label>
          <input
            v-model.number="requestForm.duration"
            type="number"
            min="15"
            max="480"
            step="15"
            placeholder="e.g., 120"
            @blur="validateDuration"
          />
          <p class="hint-text">How long do you plan to work overtime?</p>
          <p v-if="validationWarning" class="validation-warning">
            <AlertTriangle class="w-4 h-4" />
            {{ validationWarning }}
          </p>
        </div>

        <!-- Reason Input -->
        <div class="form-group">
          <label>Reason *</label>
          <textarea
            v-model="requestForm.reason"
            rows="3"
            placeholder="Explain why you need to work overtime..."
            maxlength="500"
          ></textarea>
          <p class="hint-text">{{ requestForm.reason.length }}/500 characters</p>
        </div>

        <!-- Priority Selection -->
        <div class="form-group">
          <label>Priority</label>
          <select v-model="requestForm.priority">
            <option value="normal">Normal</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>

        <!-- Photo Capture -->
        <div class="form-group">
          <label>Photo Verification *</label>
          <div class="photo-capture">
            <!-- Camera Placeholder -->
            <div v-if="!cameraActive && !photoData" class="camera-placeholder">
              <div class="camera-icon-container">
                <Camera class="w-12 h-12" />
                <div class="camera-pulse"></div>
              </div>
              <p>Take a photo to verify your overtime request</p>
              <button @click="startCamera" class="btn-start-camera">
                <Camera class="w-4 h-4" />
                Start Camera
              </button>
            </div>

            <!-- Active Camera -->
            <video v-if="cameraActive && !photoData" ref="videoElement" autoplay class="camera-preview"></video>

            <!-- Photo Preview -->
            <img v-if="photoData" :src="photoData" class="photo-preview" />

            <div v-if="cameraActive || photoData" class="photo-actions">
              <button v-if="cameraActive && !photoData" @click="capturePhoto" :disabled="!cameraReady" class="btn-capture">
                <Camera class="w-5 h-5" />
                {{ cameraReady ? 'Capture Photo' : 'Loading Camera...' }}
              </button>
              <button v-if="cameraActive && !photoData" @click="stopCamera" class="btn-secondary">
                <X class="w-4 h-4" />
                Cancel
              </button>
              <button v-if="photoData" @click="retakePhoto" class="btn-retake">
                <RotateCcw class="w-5 h-5" />
                Retake Photo
              </button>
            </div>
          </div>
        </div>

        <!-- Location Info -->
        <div class="form-group">
          <label>Location</label>
          <div class="location-status">
            <MapPin class="w-5 h-5" />
            <span v-if="locationData">
              Lat: {{ locationData.latitude.toFixed(6) }},
              Lng: {{ locationData.longitude.toFixed(6) }}
            </span>
            <span v-else>Detecting location...</span>
          </div>
        </div>

        <!-- Submit Button -->
        <button
          @click="submitRequest"
          :disabled="!canSubmit || processing"
          class="btn-submit"
        >
          <Send class="w-5 h-5" />
          {{ processing ? 'Submitting...' : 'Submit Overtime Request' }}
        </button>
      </div>
    </div>

    <!-- Recent Sessions -->
    <div class="recent-sessions">
      <div class="section-header">
        <h3>Recent Sessions</h3>
        <button @click="loadHistory" class="btn-refresh">
          <RefreshCw class="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div v-if="recentSessions.length === 0" class="empty-state">
        <Calendar class="w-12 h-12" />
        <p>No recent overtime sessions</p>
      </div>

      <div v-else class="sessions-list">
        <div
          v-for="session in recentSessions"
          :key="session.id"
          class="session-card"
        >
          <div class="session-info">
            <div class="session-date">
              {{ formatDate(session.clock_in_timestamp) }}
            </div>
            <div :class="['session-badge', session.status]">
              {{ getStatusLabel(session.status) }}
            </div>
          </div>
          <div class="session-details-mini">
            <span>Duration: {{ formatDuration(session.actual_duration_minutes || session.requested_duration_minutes) }}</span>
            <span v-if="session.status === 'payroll_approved'">
              Cost: {{ formatCurrency(session.actual_cost) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Clock Out Modal -->
    <div v-if="showClockOutModal" class="modal-overlay" @click.self="showClockOutModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Clock Out from Overtime</h3>
          <button @click="showClockOutModal = false" class="modal-close">
            <X class="w-6 h-6" />
          </button>
        </div>

        <div class="modal-body">
          <p>Take a photo to verify your clock-out</p>

          <div class="photo-capture">
            <video v-if="!clockOutPhoto" ref="clockOutVideoElement" autoplay class="camera-preview"></video>
            <img v-else :src="clockOutPhoto" class="photo-preview" />

            <div class="photo-actions">
              <button v-if="!clockOutPhoto" @click="captureClockOutPhoto" :disabled="!clockOutCameraReady" class="btn-capture">
                <Camera class="w-5 h-5" />
                {{ clockOutCameraReady ? 'Capture Photo' : 'Loading Camera...' }}
              </button>
              <button v-else @click="retakeClockOutPhoto" class="btn-retake">
                <RotateCcw class="w-5 h-5" />
                Retake Photo
              </button>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="showClockOutModal = false" class="btn-cancel">
            Cancel
          </button>
          <button
            @click="clockOut"
            :disabled="!clockOutPhoto || processing"
            class="btn-confirm"
          >
            <LogOut class="w-5 h-5" />
            {{ processing ? 'Clocking Out...' : 'Confirm Clock Out' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useCurrency } from '@/composables/useCurrency';
const { formatCurrency } = useCurrency();
import {
  Clock,
  Timer,
  MapPin,
  AlarmClock,
  DollarSign,
  LogOut,
  AlertCircle,
  CheckCircle,
  X,
  Camera,
  RotateCcw,
  Send,
  RefreshCw,
  Calendar,
  AlertTriangle,
} from 'lucide-vue-next';
import { api } from '@/services/api';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

// Reactive state
const activeSession = ref(null);
const recentSessions = ref([]);
const requestForm = ref({
  duration: 120,
  reason: '',
  priority: 'normal'
});
const photoData = ref(null);
const clockOutPhoto = ref(null);
const locationData = ref(null);
const cameraActive = ref(false);
const cameraReady = ref(false);
const clockOutCameraReady = ref(false);
const processing = ref(false);
const message = ref('');
const messageType = ref('success');
const showClockOutModal = ref(false);
const validationWarning = ref('');
const timeRemaining = ref('00:00:00');
const expiryWarningVisible = ref(false);
const EXPIRY_WARNING_MINUTES = 10;

// OA-006: Activation state for pre-scheduled sessions
const activationPhoto = ref(null);
const activationCameraActive = ref(false);
const activationCameraReady = ref(false);
const activationVideoElement = ref(null);
let activationVideoStream = null;

// Refs for video elements
const videoElement = ref(null);
const clockOutVideoElement = ref(null);
let videoStream = null;
let clockOutVideoStream = null;
let timerInterval = null;

// Computed
const canSubmit = computed(() => {
  return requestForm.value.duration >= 15 &&
    requestForm.value.reason.trim().length > 0 &&
    photoData.value &&
    locationData.value &&
    !processing.value;
});

// OA-006: Computed for activation
const canActivate = computed(() => {
  return activationPhoto.value && locationData.value && !processing.value;
});

const isWithinActivationWindow = computed(() => {
  if (!activeSession.value || activeSession.value.status !== 'approved' || activeSession.value.mode !== 'pre_scheduled') {
    return false;
  }
  const now = new Date();
  const scheduledDate = activeSession.value.scheduled_date?.split('T')[0] || activeSession.value.scheduled_date;
  const startStr = `${scheduledDate}T${activeSession.value.scheduled_start_time}`;
  const endStr = `${scheduledDate}T${activeSession.value.scheduled_end_time}`;
  let scheduledStart = new Date(startStr);
  let scheduledEnd = new Date(endStr);
  // Handle overnight
  if (scheduledEnd <= scheduledStart) {
    scheduledEnd.setDate(scheduledEnd.getDate() + 1);
  }
  const windowStart = new Date(scheduledStart.getTime() - 15 * 60 * 1000);
  return now >= windowStart && now <= scheduledEnd;
});

// Methods
const loadActiveSession = async () => {
  try {
    const response = await api.get('/employee/overtime-clock/active');
    if (response.data.data) {
      activeSession.value = response.data.data;
      if (activeSession.value.status === 'active') {
        startTimer();
      }
    }
  } catch (error) {
    console.error('Failed to load active session:', error);
  }
};

const loadHistory = async () => {
  try {
    const response = await api.get('/employee/overtime-clock/history', {
      params: { limit: 5 }
    });
    recentSessions.value = response.data.data.sessions || [];
  } catch (error) {
    console.error('Failed to load history:', error);
  }
};

const validateDuration = async () => {
  if (!requestForm.value.duration || requestForm.value.duration < 15) {
    validationWarning.value = '';
    return;
  }

  try {
    const response = await api.get('/employee/overtime-clock/validate', {
      params: { duration: requestForm.value.duration }
    });

    if (!response.data.data.valid) {
      validationWarning.value = response.data.data.warning;
    } else {
      validationWarning.value = '';
    }
  } catch (error) {
    console.error('Validation failed:', error);
  }
};

const startCamera = async () => {
  try {
    // First, activate the camera UI (renders the video element)
    cameraActive.value = true;

    // Wait for Vue to update the DOM and render the video element
    await new Promise(resolve => setTimeout(resolve, 100));

    // Now request camera access
    videoStream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user' }
    });

    // Set the video source
    if (videoElement.value) {
      videoElement.value.srcObject = videoStream;
      cameraReady.value = true;
    } else {
      console.error('Video element not found in DOM');
      showMessage('Camera initialization failed', 'error');
      cameraActive.value = false;
    }
  } catch (error) {
    console.error('Camera error:', error);
    showMessage(error.name === 'NotAllowedError' ? 'Camera access denied' : 'Camera not available', 'error');
    cameraActive.value = false;
  }
};

const stopCamera = () => {
  if (videoStream) {
    videoStream.getTracks().forEach(track => track.stop());
    videoStream = null;
  }
  cameraActive.value = false;
  cameraReady.value = false;
};

const startClockOutCamera = async () => {
  try {
    clockOutVideoStream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user' }
    });
    if (clockOutVideoElement.value) {
      clockOutVideoElement.value.srcObject = clockOutVideoStream;
      clockOutCameraReady.value = true;
    }
  } catch (error) {
    showMessage('Camera access denied', 'error');
  }
};

const capturePhoto = () => {
  if (!videoElement.value) return;

  const canvas = document.createElement('canvas');
  canvas.width = videoElement.value.videoWidth;
  canvas.height = videoElement.value.videoHeight;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(videoElement.value, 0, 0);
  photoData.value = canvas.toDataURL('image/jpeg', 0.8);

  // Stop camera
  stopCamera();
};

const captureClockOutPhoto = () => {
  if (!clockOutVideoElement.value) return;

  const canvas = document.createElement('canvas');
  canvas.width = clockOutVideoElement.value.videoWidth;
  canvas.height = clockOutVideoElement.value.videoHeight;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(clockOutVideoElement.value, 0, 0);
  clockOutPhoto.value = canvas.toDataURL('image/jpeg', 0.8);

  // Stop camera
  if (clockOutVideoStream) {
    clockOutVideoStream.getTracks().forEach(track => track.stop());
    clockOutVideoStream = null;
  }
  clockOutCameraReady.value = false;
};

const retakePhoto = () => {
  photoData.value = null;
  cameraActive.value = false;
  cameraReady.value = false;
};

const retakeClockOutPhoto = () => {
  clockOutPhoto.value = null;
  setTimeout(() => startClockOutCamera(), 100);
};

const getLocation = () => {
  if ('geolocation' in navigator) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        locationData.value = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        };
      },
      (error) => {
        showMessage('Location access denied', 'error');
      }
    );
  }
};

const submitRequest = async () => {
  if (!canSubmit.value) return;

  processing.value = true;
  try {
    // Convert base64 to blob
    const photoBlob = await fetch(photoData.value).then(r => r.blob());

    const formData = new FormData();
    formData.append('photo', photoBlob, 'overtime-clock-in.jpg');
    formData.append('requestedDurationMinutes', requestForm.value.duration);
    formData.append('reason', requestForm.value.reason);
    formData.append('priority', requestForm.value.priority);
    formData.append('latitude', locationData.value.latitude);
    formData.append('longitude', locationData.value.longitude);
    formData.append('accuracy', locationData.value.accuracy);
    formData.append('clientTimestamp', new Date().toISOString());

    const response = await api.post('/employee/overtime-clock/request', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    showMessage(response.data.message || 'Request submitted successfully!', 'success');

    // Reset form
    requestForm.value = { duration: 120, reason: '', priority: 'normal' };
    photoData.value = null;

    // Reload active session
    await loadActiveSession();
    await loadHistory();
  } catch (error) {
    showMessage(error.response?.data?.message || 'Failed to submit request', 'error');
  } finally {
    processing.value = false;
  }
};

const clockOut = async () => {
  if (!clockOutPhoto.value || !activeSession.value) return;

  processing.value = true;
  try {
    const photoBlob = await fetch(clockOutPhoto.value).then(r => r.blob());

    const formData = new FormData();
    formData.append('photo', photoBlob, 'overtime-clock-out.jpg');
    formData.append('latitude', locationData.value.latitude);
    formData.append('longitude', locationData.value.longitude);
    formData.append('accuracy', locationData.value.accuracy);
    formData.append('clientTimestamp', new Date().toISOString());

    const response = await api.post(
      `/employee/overtime-clock/${activeSession.value.id}/clock-out`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    showMessage(response.data.message || 'Clocked out successfully!', 'success');
    showClockOutModal.value = false;
    clockOutPhoto.value = null;

    // Reload
    await loadActiveSession();
    await loadHistory();
  } catch (error) {
    showMessage(error.response?.data?.message || 'Failed to clock out', 'error');
  } finally {
    processing.value = false;
  }
};

const cancelRequest = async (sessionId) => {
  if (!confirm('Are you sure you want to cancel this overtime request?')) return;

  try {
    await api.delete(`/employee/overtime-clock/${sessionId}/cancel`);
    showMessage('Request cancelled successfully', 'success');
    await loadActiveSession();
    await loadHistory();
  } catch (error) {
    showMessage('Failed to cancel request', 'error');
  }
};

// OA-006: Activation camera methods
const startActivationCamera = async () => {
  try {
    activationCameraActive.value = true;
    await new Promise(resolve => setTimeout(resolve, 100));
    activationVideoStream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user' }
    });
    if (activationVideoElement.value) {
      activationVideoElement.value.srcObject = activationVideoStream;
      activationCameraReady.value = true;
    } else {
      showMessage('Camera initialization failed', 'error');
      activationCameraActive.value = false;
    }
  } catch (error) {
    showMessage(error.name === 'NotAllowedError' ? 'Camera access denied' : 'Camera not available', 'error');
    activationCameraActive.value = false;
  }
};

const captureActivationPhoto = () => {
  if (!activationVideoElement.value) return;
  const canvas = document.createElement('canvas');
  canvas.width = activationVideoElement.value.videoWidth;
  canvas.height = activationVideoElement.value.videoHeight;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(activationVideoElement.value, 0, 0);
  activationPhoto.value = canvas.toDataURL('image/jpeg', 0.8);
  // Stop camera
  if (activationVideoStream) {
    activationVideoStream.getTracks().forEach(track => track.stop());
    activationVideoStream = null;
  }
  activationCameraActive.value = false;
  activationCameraReady.value = false;
};

const retakeActivationPhoto = () => {
  activationPhoto.value = null;
  activationCameraActive.value = false;
  activationCameraReady.value = false;
};

const activateSession = async () => {
  if (!canActivate.value || !activeSession.value) return;

  processing.value = true;
  try {
    const photoBlob = await fetch(activationPhoto.value).then(r => r.blob());

    const formData = new FormData();
    formData.append('photo', photoBlob, 'overtime-activation.jpg');
    formData.append('latitude', locationData.value.latitude);
    formData.append('longitude', locationData.value.longitude);
    formData.append('accuracy', locationData.value.accuracy);
    formData.append('clientTimestamp', new Date().toISOString());

    const response = await api.post(
      `/employee/overtime-clock/${activeSession.value.id}/activate`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    showMessage(response.data.message || 'Overtime session activated!', 'success');
    activationPhoto.value = null;

    await loadActiveSession();
    await loadHistory();
  } catch (error) {
    showMessage(error.response?.data?.message || 'Failed to activate session', 'error');
  } finally {
    processing.value = false;
  }
};

const startTimer = () => {
  if (timerInterval) clearInterval(timerInterval);

  // Use server-provided remaining_seconds to calculate end time
  // (timer_end_time is Palestine time but returned with Z suffix, causing timezone mismatch)
  const serverRemainingMs = (activeSession.value.timeRemaining || activeSession.value.remaining_seconds || 0) * 1000;
  const clientEndTime = Date.now() + serverRemainingMs;

  timerInterval = setInterval(() => {
    if (!activeSession.value) return;

    const now = Date.now();
    const diff = clientEndTime - now;

    if (diff <= 0) {
      timeRemaining.value = '00:00:00';
      expiryWarningVisible.value = false;
      clearInterval(timerInterval);
      loadActiveSession(); // Reload to check if auto-clocked-out
    } else {
      const hours = Math.floor(diff / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      timeRemaining.value = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

      // UX-ATT-003: Show warning when timer is within EXPIRY_WARNING_MINUTES
      const totalMinutesLeft = diff / 60000;
      expiryWarningVisible.value = totalMinutesLeft <= EXPIRY_WARNING_MINUTES;
    }
  }, 1000);
};

const showMessage = (msg, type = 'success') => {
  message.value = msg;
  messageType.value = type;
  setTimeout(() => clearMessage(), 5000);
};

const clearMessage = () => {
  message.value = '';
};

const getStatusLabel = (status) => {
  const labels = {
    pending: 'Pending Approval',
    approved: 'Approved - Awaiting Activation',
    active: 'Active - Timer Running',
    under_review: 'Under Review',
    expired: 'Expired',
    rejected: 'Rejected',
    completed: 'Completed',
    payroll_approved: 'Approved for Payroll'
  };
  return labels[status] || status;
};

const formatTime = (timestamp) => {
  if (!timestamp) return '-';
  // Timestamps are stored as Palestine time but returned with Z suffix by mysql2;
  // use .utc() to display the raw stored value (which IS Palestine time)
  return dayjs.utc(timestamp).format('h:mm A');
};

const formatDate = (timestamp) => {
  if (!timestamp) return '-';
  return dayjs.utc(timestamp).format('MMM D, YYYY h:mm A');
};

const formatScheduledDate = (dateStr) => {
  if (!dateStr) return '-';
  // scheduled_date comes as "2026-02-16T00:00:00.000Z" — extract just the date
  const d = dateStr.split('T')[0] || dateStr;
  return dayjs(d).format('MMM D, YYYY');
};

const formatDuration = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

// Lifecycle
onMounted(async () => {
  getLocation();
  await loadActiveSession();
  await loadHistory();
});

onUnmounted(() => {
  if (videoStream) {
    videoStream.getTracks().forEach(track => track.stop());
  }
  if (clockOutVideoStream) {
    clockOutVideoStream.getTracks().forEach(track => track.stop());
  }
  if (activationVideoStream) {
    activationVideoStream.getTracks().forEach(track => track.stop());
  }
  if (timerInterval) {
    clearInterval(timerInterval);
  }
});

// Watch for modal open to start clock-out camera
watch(showClockOutModal, (isOpen) => {
  if (isOpen && !clockOutCameraReady.value) {
    setTimeout(() => startClockOutCamera(), 100);
  }
});
</script>

<style scoped>
.overtime-clock {
  max-width: 800px;
  margin: 0 auto;
  padding: 0;
}

.active-session-card {
  background: linear-gradient(135deg, #6366f1 0%, #7c3aed 100%);
  border: 1px solid rgba(129, 140, 248, 0.35);
  border-radius: 16px;
  padding: 2rem;
  color: white;
  margin-bottom: 2rem;
  box-shadow: 0 10px 40px rgba(99, 102, 241, 0.25);
}

.session-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  font-weight: 600;
}

.status-indicator.pending {
  background: rgba(251, 191, 36, 0.3);
}

.status-indicator.active {
  background: rgba(34, 197, 94, 0.3);
}

.timer-display {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.timer-content h2 {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
  font-variant-numeric: tabular-nums;
}

.timer-content p {
  margin: 0;
  opacity: 0.9;
  font-size: 0.875rem;
}

.session-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.action-section {
  text-align: center;
}

.btn-clock-out {
  background: white;
  color: #6366f1;
  border: none;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.btn-clock-out:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

.btn-clock-out:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.hint-text {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  opacity: 0.9;
}

.pending-message,
.review-message {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
}

/* OA-006: Activation section for approved pre-scheduled sessions */
.activation-section {
  padding: 1rem;
}

.activation-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: rgba(34, 197, 94, 0.15);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 12px;
}

.activation-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.activation-prompt {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
}

.activation-window-info {
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.7);
}

.btn-activate {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-activate:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.4);
}

.btn-activate:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.location-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
}

.location-pending {
  opacity: 0.6;
}

/* UX-ATT-003: Expiry Warning */
.expiry-warning {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(239, 68, 68, 0.2);
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 12px;
  margin-bottom: 1.5rem;
  animation: warningPulse 2s ease-in-out infinite;
}

@keyframes warningPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.8; }
}

.expiry-warning .font-semibold {
  font-weight: 600;
  margin: 0 0 0.25rem 0;
}

.expiry-warning .text-sm {
  font-size: 0.875rem;
  opacity: 0.9;
  margin: 0;
}

.btn-cancel-small {
  background: rgba(239, 68, 68, 0.3);
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-cancel-small:hover {
  background: rgba(239, 68, 68, 0.5);
}

.clock-in-section {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
}

.section-header {
  margin-bottom: 2rem;
}

.section-header h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0 0 0.5rem 0;
}

.section-header p {
  color: #94a3b8;
  margin: 0;
}

.message {
  padding: 1rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.message.success {
  background: rgba(52, 211, 153, 0.09);
  border: 1px solid rgba(52, 211, 153, 0.3);
  color: #6ee7b7;
}

.message.error {
  background: rgba(248, 113, 113, 0.09);
  border: 1px solid rgba(248, 113, 113, 0.3);
  color: #fca5a5;
}

.message-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.message-close {
  background: none;
  border: none;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.message-close:hover {
  opacity: 1;
}

.request-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group label {
  display: block;
  font-weight: 600;
  color: #94a3b8;
  margin-bottom: 0.5rem;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  font-size: 1rem;
  background: rgba(255, 255, 255, 0.04);
  color: #e2e8f0;
  transition: border-color 0.2s;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: rgba(99, 102, 241, 0.6);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.form-group .hint-text {
  font-size: 0.875rem;
  color: #94a3b8;
  margin-top: 0.5rem;
}

.validation-warning {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #f59e0b;
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

.photo-capture {
  border: 2px dashed rgba(255, 255, 255, 0.14);
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
}

.camera-placeholder {
  padding: 2rem;
  text-align: center;
}

.camera-icon-container {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
}

.camera-pulse {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: #6366f1;
  opacity: 0.25;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.2;
  }
  50% {
    transform: scale(1.2);
    opacity: 0;
  }
}

.camera-placeholder p {
  color: #94a3b8;
  margin-bottom: 1rem;
  font-size: 0.95rem;
}

.btn-start-camera {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #6366f1;
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-start-camera:hover {
  background: #4f46e5;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.camera-preview,
.photo-preview {
  width: 100%;
  max-width: 400px;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.photo-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.btn-capture,
.btn-retake,
.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-capture {
  background: #6366f1;
  color: white;
}

.btn-capture:hover:not(:disabled) {
  background: #4f46e5;
}

.btn-capture:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-retake {
  background: rgba(255, 255, 255, 0.08);
  color: #cbd5e1;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-retake:hover {
  background: rgba(255, 255, 255, 0.12);
}

.location-status {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  color: #cbd5e1;
}

.btn-submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, #6366f1 0%, #7c3aed 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(99, 102, 241, 0.4);
}

.btn-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.recent-sessions {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 16px;
  padding: 2rem;
}

.recent-sessions .section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.btn-refresh {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  font-weight: 600;
  color: #cbd5e1;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-refresh:hover {
  background: rgba(255, 255, 255, 0.09);
}

.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #64748b;
}

.empty-state svg {
  margin: 0 auto 1rem;
  opacity: 0.5;
}

.sessions-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.session-card {
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.02);
  transition: all 0.2s ease;
}

.session-card:hover {
  border-color: rgba(99, 102, 241, 0.45);
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.12);
}

.session-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.session-date {
  font-weight: 600;
  color: #e2e8f0;
}

.session-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.session-badge.pending {
  background: rgba(251, 191, 36, 0.12);
  border: 1px solid rgba(251, 191, 36, 0.3);
  color: #fcd34d;
}

.session-badge.active {
  background: rgba(52, 211, 153, 0.12);
  border: 1px solid rgba(52, 211, 153, 0.3);
  color: #6ee7b7;
}

.session-badge.under_review {
  background: rgba(56, 189, 248, 0.12);
  border: 1px solid rgba(56, 189, 248, 0.3);
  color: #7dd3fc;
}

.session-badge.payroll_approved {
  background: rgba(52, 211, 153, 0.12);
  border: 1px solid rgba(52, 211, 153, 0.3);
  color: #6ee7b7;
}

.session-badge.rejected {
  background: rgba(248, 113, 113, 0.12);
  border: 1px solid rgba(248, 113, 113, 0.3);
  color: #fca5a5;
}

.session-details-mini {
  display: flex;
  gap: 1rem;
  font-size: 0.875rem;
  color: #94a3b8;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(2, 4, 12, 0.72);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: rgba(11, 16, 31, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 20px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

.modal-header h3 {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  color: #f1f5f9;
}

.modal-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  transition: color 0.2s;
}

.modal-close:hover {
  color: #e2e8f0;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
}

.btn-cancel,
.btn-confirm {
  flex: 1;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-cancel {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #cbd5e1;
}

.btn-cancel:hover {
  background: rgba(255, 255, 255, 0.09);
}

.btn-confirm {
  background: #6366f1;
  color: white;
}

.btn-confirm:hover:not(:disabled) {
  background: #4f46e5;
}

.btn-confirm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 640px) {
  .overtime-clock {
    padding: 0;
  }

  .active-session-card,
  .clock-in-section,
  .recent-sessions {
    padding: 1rem;
  }

  .session-header {
    flex-direction: column;
    gap: 1rem;
  }

  .session-details {
    grid-template-columns: 1fr;
  }

  .timer-content h2 {
    font-size: 2rem;
  }
}
</style>
